import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/common/components/ui/button';
import { PaginationControls } from '@/common/components/ui/pagination-controls';
import { Skeleton } from '@/common/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@/common/components/ui/table';
import { formatLastUpdated } from '@/common/utils/DateFormatters';
import { ScrollShadows } from '@/policy/component/ScrollShadows/ScrollShadows';
import { useScroll } from '@/policy/hooks/useScroll';

import { BillingFilters, BillingSummary } from '../api/FetchBillingList';
import { useAssignBillings } from '../hooks/useAssignBillings';
import { useToggleLockBilling } from '../hooks/useToggleLockBilling';
import { BillingSortField, SortOrder } from '../types/SortTypes';
import { BillingDrawer } from './BillingDrawer';
import { BillingHeader } from './BillingHeader';
import { BillingListSkeleton } from './BillingListSkeleton';
import { BillingRow } from './BillingRow';

interface BillingListProps {
  billings: BillingSummary[];
  isLoading: boolean;
  totalPages: number;
  totalBillings: number;
  currentPage: number;
  pageSize: number;
  sortBy: BillingSortField;
  sortOrder: SortOrder;
  filters: BillingFilters;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSort: (field: BillingSortField) => void;
  onFilterChange: (field: string, value: string | string[]) => void;
  onRefresh: () => void;
  lastRefreshedAt: Date;
}

export const BillingList = ({
  billings,
  isLoading,
  totalPages,
  totalBillings,
  currentPage,
  pageSize,
  sortBy,
  sortOrder,
  filters,
  onPageChange,
  onPageSizeChange,
  onSort,
  onFilterChange,
  onRefresh,
  lastRefreshedAt,
}: BillingListProps) => {
  const { t, i18n } = useTranslation();
  const {
    scrollContainerRef,
    canScrollLeft,
    canScrollRight,
    canScrollDown,
    canScrollUp,
  } = useScroll();
  const [isSpinning, setIsSpinning] = useState(false);
  const [checkedBillings, setCheckedBillings] = useState<string[]>([]);
  const [selectedBilling, setSelectedBilling] = useState<BillingSummary | null>(
    null,
  );
  const [selectedBillingIndex, setSelectedBillingIndex] = useState<
    number | null
  >(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { assign } = useAssignBillings({
    billingUuids: checkedBillings,
    onSuccess: () => {
      setCheckedBillings([]);
    },
    onError: () => {},
  });

  const { toggleLockBilling } = useToggleLockBilling({
    onSuccess: () => {},
    onError: () => {},
  });

  const handleRefresh = () => {
    setIsSpinning(true);
    onRefresh();
    setTimeout(() => {
      setIsSpinning(false);
    }, 2000);
  };

  const handleAssignBillings = () => {
    setIsSpinning(true);
    assign();
    setTimeout(() => {
      setIsSpinning(false);
    }, 2000);
  };

  const handleRowClick = (billing: BillingSummary) => {
    toggleLockBilling(billing.uuid);
    setSelectedBilling(billing);
    const newSelectedIndex = billings.findIndex((b) => b.uuid === billing.uuid);
    setSelectedBillingIndex(newSelectedIndex);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    if (selectedBilling) {
      toggleLockBilling(selectedBilling.uuid);
      setSelectedBilling(null);
      setSelectedBillingIndex(null);
    }
  };

  const handleNavigatePrevious = () => {
    if (selectedBilling) {
      toggleLockBilling(selectedBilling.uuid);
      if (selectedBillingIndex !== null && selectedBillingIndex > 0) {
        const newSelectedBilling = billings[selectedBillingIndex - 1];
        toggleLockBilling(newSelectedBilling.uuid);
        setSelectedBilling(newSelectedBilling);
        setSelectedBillingIndex(selectedBillingIndex - 1);
      }
    }
  };

  const handleNavigateNext = () => {
    if (selectedBilling) {
      toggleLockBilling(selectedBilling.uuid);
      if (
        selectedBillingIndex !== null &&
        selectedBillingIndex < billings.length - 1
      ) {
        const newSelectedBilling = billings[selectedBillingIndex + 1];
        toggleLockBilling(newSelectedBilling.uuid);
        setSelectedBilling(newSelectedBilling);
        setSelectedBillingIndex(selectedBillingIndex + 1);
      }
    }
  };

  const hasPrevious = selectedBillingIndex !== null && selectedBillingIndex > 0;
  const hasNext =
    selectedBillingIndex !== null && selectedBillingIndex < billings.length - 1;

  return (
    <div className="flex flex-col h-full w-full">
      <div className="flex flex-row items-center justify-end gap-x-2 pb-4 shrink-0">
        {isLoading ? (
          <>
            <Skeleton className="h-4 w-48 bg-muted-foreground/20" />
            <Skeleton className="h-8 w-8 bg-muted-foreground/20 rounded-md" />
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground text-xs font-light leading-4">
              {t('common.lastUpdatedAt', {
                updatedAt: formatLastUpdated(lastRefreshedAt, i18n.language),
              })}
            </p>
            <Button
              aria-label={t('common.refresh')}
              onClick={handleRefresh}
              disabled={isSpinning}
              variant="secondary"
              size="sm"
            >
              <RefreshCw
                className={`h-4 w-4 ${isSpinning && 'animate-spin'}`}
              />
            </Button>
          </>
        )}
      </div>
      {checkedBillings.length > 0 && (
        <div className="flex flex-row items-center justify-between py-2 px-4 rounded-md bg-primary mb-4 text-white">
          {checkedBillings.length} {t('billing.selectedEdocs')}
          <Button
            className="bg-white text-black border hover:border-white hover:text-white"
            onClick={handleAssignBillings}
          >
            {t('billing.assignToMyself')}
          </Button>
        </div>
      )}

      <ScrollShadows
        canScrollLeft={canScrollLeft}
        canScrollRight={canScrollRight}
        canScrollDown={canScrollDown}
        canScrollUp={canScrollUp}
        className="rounded-md border border-border bg-background shadow-sm overflow-hidden flex-1 flex flex-col min-h-0 min-w-0"
        offsets={{
          top: 40,
          left: 0,
          bottom: 0,
          right: 0,
        }}
      >
        <div
          ref={scrollContainerRef}
          className={`overflow-x-auto overflow-y-auto flex-1 z-0
          [&::-webkit-scrollbar]:h-1.5
          [&::-webkit-scrollbar]:w-1.5
          [&::-webkit-scrollbar-track]:bg-transparent
          [&::-webkit-scrollbar-thumb]:bg-warm-gray-400
          [&::-webkit-scrollbar-thumb]:rounded-full`}
        >
          <Table noWrapper className="w-full min-w-full">
            <BillingHeader
              sortBy={sortBy}
              sortOrder={sortOrder}
              filters={filters}
              onSort={onSort}
              onFilterChange={onFilterChange}
            />
            {isLoading ? (
              <BillingListSkeleton rowCount={pageSize} />
            ) : (
              <TableBody>
                {billings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={17} className="text-center">
                      {t('billing.noBillingsFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  billings.map((billing) => (
                    <BillingRow
                      key={billing.uuid}
                      billing={billing}
                      selectedBillings={checkedBillings}
                      setSelectedBillings={setCheckedBillings}
                      onRowClick={handleRowClick}
                    />
                  ))
                )}
              </TableBody>
            )}
          </Table>
        </div>
      </ScrollShadows>

      <div className="pt-4 shrink-0">
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalBillings}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      </div>

      {selectedBilling && (
        <BillingDrawer
          billing={selectedBilling}
          isOpen={isDrawerOpen}
          onClose={handleCloseDrawer}
          onNavigatePrevious={handleNavigatePrevious}
          onNavigateNext={handleNavigateNext}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
        />
      )}
    </div>
  );
};
